/**
 * Catch-all route handler for single-segment unknown routes
 * Redirects any unknown routes to:
 * - /index if user is signed in
 * - /sign-in if user is not signed in
 *
 * This handles routes like:
 * - /blabla
 * - /any-unknown-path
 *
 * Works for both:
 * - localhost:8081/blabla (development)
 * - zporti.com/blabla (production)
 */

import axios from 'axios';
import { useSupabaseAuth } from '../../src/shared/contexts/SupabaseAuthContext';
import { Redirect, useLocalSearchParams } from 'expo-router';

export default function CatchAllRoute() {
  const { isSignedIn, isLoaded } = useSupabaseAuth();
  const params = useLocalSearchParams();

  // #region agent log
  axios
    .post('http://127.0.0.1:7242/ingest/9e4eaf9a-57cb-4a76-9cd7-47960f0d2f7c', {
      location: '[slug].tsx:20',
      message: 'CatchAllRoute rendered',
      data: { slug: params.slug, isLoaded },
      timestamp: Date.now(),
      sessionId: 'debug-session',
      runId: 'run1',
      hypothesisId: 'A',
    })
    .catch(() => {
      // Ignore errors
    });
  // #endregion

  // Wait for auth to finish loading
  if (!isLoaded) {
    return null;
  }

  // Check if this is a known route (shouldn't happen, but just in case)
  const slug = params.slug as string;
  const knownRoutes = [
    'index',
    'create',
    'friends',
    'gameResults',
    'invite',
    'tutorial',
    'oauth-native-callback',
    'oauth-gmail-callback',
    'landing',
  ];

  // #region agent log
  axios
    .post('http://127.0.0.1:7242/ingest/9e4eaf9a-57cb-4a76-9cd7-47960f0d2f7c', {
      location: '[slug].tsx:45',
      message: 'Route check',
      data: { slug, knownRoutes, isKnown: knownRoutes.includes(slug) },
      timestamp: Date.now(),
      sessionId: 'debug-session',
      runId: 'run1',
      hypothesisId: 'A',
    })
    .catch(() => {
      // Ignore errors
    });
  // #endregion

  // If it's a known route, don't redirect (shouldn't happen)
  if (knownRoutes.includes(slug)) {
    return null;
  }

  // Redirect based on authentication status
  // If signed in, redirect to home page
  if (isSignedIn) {
    return <Redirect href="/app/" />;
  }

  // If not signed in, redirect to sign-in page
  return <Redirect href="/sign-in" />;
}
